import { Axios, AxiosRequestConfig } from "axios";
import { RequestFormFieldDataInput } from "../Schemas/RequestFormFieldDataInput";
import { RequestFormFieldDataOutput, RequestFormFieldDataOutputSchema } from "../Schemas/RequestFormFieldDataOutput";
import { removeFalsyPropertiesFromObject } from "vig-ui-library";
import { RequestHelperGetPrepopInfoOutput, RequestHelperGetPrepopInfoOutputSchema } from "../Schemas/RequestHelperGetPrepopInfoOutput";
import { RequestHelperGetPrepopInfoInput } from "../Schemas/RequestHelperGetPrepopInfoInput";
import { RequestHelperGetContractPrepopInfoOutput, RequestHelperGetContractPrepopInfoOutputSchema } from "../Schemas/RequestHelperGetContractPrepopInfoOutput";
import { RequestHelperGetContractPrepopInfoInput } from "../Schemas/RequestHelperGetContractPrepopInfoInput";
import { RequestHelperGetPartnerPrepopInfoOutput, RequestHelperGetPartnerPrepopInfoOutputSchema } from "../Schemas/RequestHelperGetPartnerPrepopInfoOutput";
import { RequestHelperGetPartnerPrepopInfoInput } from "../Schemas/RequestHelperGetPartnerPrepopInfoInput";
import { GetRequestTypesWithTagsOutput, GetRequestTypesWithTagsOutputSchema } from "../Schemas/GetRequestTypesWithTagsOutput";
import { GetRequestTypesWithTagsInput } from "../Schemas/GetRequestTypesWithTagsInput";
import { RequestDetailOutput, RequestDetailOutputSchema } from "../Schemas/RequestDetailOutput";
import { RequestDetailInput } from "../Schemas/RequestDetailInput";
import { CreatedRequestOutput, CreatedRequestOutputSchema } from "../Schemas/CreatedRequestOutput";
import { InsertRequestInput } from "../Schemas/InsertRequestInput";
import { DeleteUploadedFileInput } from "../Schemas/DeleteUploadedFileInput";
import { DeleteUploadedFileOutput, DeleteUploadedFileOutputSchema } from "../Schemas/DeleteUploadedFileOutput";
import { ScanAttachmentWithAIInput } from "../Schemas/ScanAttachmentWithAIInput";
import { ScanAttachmentWithAIOutput, ScanAttachmentWithAIOutputSchema } from "../Schemas/ScanAttachmentWithAIOutput";
import { ChangeFileTaxonomyOutput, ChangeFileTaxonomyOutputSchema } from "../Schemas/ChangeFileTaxonomyOutput";
import { ChangeFileTaxonomyInput } from "../Schemas/ChangeFileTaxonomyInput";
import { GetRequestsStatesInput } from "../Schemas/GetRequestsStatesInput";
import { GetMagicLinkConfigInput } from "../Schemas/GetMagicLinkConfigInput";
import { GetMagicLinkConfigOutput, GetMagicLinkConfigOutputSchema } from "../Schemas/GetMagicLinkConfigOutput";
import { CreateMagicLinkInput } from "../Schemas/CreateMagicLinkInput";
import { CreateMagicLinkOutput, CreateMagicLinkOutputSchema } from "../Schemas/CreateMagicLinkOutput";
import { UploadIdCardInput } from "../Schemas/UploadIdCardInput";
import { UploadIdCardOutput, UploadIdCardOutputSchema } from "../Schemas/UploadIdCardOutput";
import { StartMagicLinkProcessInput } from "../Schemas/StartMagicLinkProcessInput";
import { CancelMagicLinkProcessInput } from "../Schemas/CancelMagicLinkProcessInput";
import { BaseOutput, BaseOutputSchema } from "../Schemas/BaseOutput";
import { GetAllRequestsOutput, GetAllRequestsOutputSchema } from "../Schemas/GetAllRequestsOutput";
import { GetAllRequestsInput } from "../Schemas/GetAllRequestsInput";
import { RequestFieldsRelationInput } from "../Schemas/RequestFieldsRelationInput";
import { RequestFieldsRelationOutput, RequestFieldsRelationOutputSchema } from "../Schemas/RequestFieldsRelationOutput";
import { RequestApiRouteConfig, resolveRouteConfig } from "./routeConfig";

export type { RequestApiRouteConfig } from "./routeConfig";

export const useRequestApi = (axiosInstance: Axios, routeConfig?: RequestApiRouteConfig) => {
    const routes = resolveRouteConfig(routeConfig);

    const getFormFieldData = async (input: RequestFormFieldDataInput, config?: AxiosRequestConfig): Promise<RequestFormFieldDataOutput> => {
        const url = `${routes.requestBasePath}/getFormFieldData`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return RequestFormFieldDataOutputSchema.parse(response.data);
    }

    const getRequestPrepopInfo = async (input: RequestHelperGetPrepopInfoInput, config?: AxiosRequestConfig): Promise<RequestHelperGetPrepopInfoOutput> => {
        const url = `${routes.requestBasePath}/getRequestPrepopInfo`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return RequestHelperGetPrepopInfoOutputSchema.parse(response.data);
    }

    const getRequestContractPrepopInfo = async (input: RequestHelperGetContractPrepopInfoInput, config?: AxiosRequestConfig): Promise<RequestHelperGetContractPrepopInfoOutput> => {
        const url = `${routes.requestBasePath}/getRequestContractPrepopInfo`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return RequestHelperGetContractPrepopInfoOutputSchema.parse(response.data);
    }

    const getRequestPartnerPrepopInfo = async (input: RequestHelperGetPartnerPrepopInfoInput, config?: AxiosRequestConfig): Promise<RequestHelperGetPartnerPrepopInfoOutput> => {
        const url = `${routes.requestBasePath}/getRequestPartnerPrepopInfo`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return RequestHelperGetPartnerPrepopInfoOutputSchema.parse(response.data);
    }

    const getAllRequestsTypes = async (input: GetRequestTypesWithTagsInput, config?: AxiosRequestConfig): Promise<GetRequestTypesWithTagsOutput> => {
        const url = `${routes.requestBasePath}/GetAllRequestsTypes`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return GetRequestTypesWithTagsOutputSchema.parse(response.data);
    }

    const getDetail = async (input: RequestDetailInput, config?: AxiosRequestConfig): Promise<RequestDetailOutput> => {
        const url = `${routes.requestBasePath}/getDetail`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return RequestDetailOutputSchema.parse(response.data);
    }

    const saveOrUpdateRequest = async (input: InsertRequestInput, config?: AxiosRequestConfig): Promise<CreatedRequestOutput> => {
        const url = `${routes.requestBasePath}/saveOrUpdateRequest`;
        const response = await axiosInstance.post(url, input, config);
        return CreatedRequestOutputSchema.parse(response.data);
    }

    const deleteUploadedFile = async (input: DeleteUploadedFileInput, config?: AxiosRequestConfig): Promise<DeleteUploadedFileOutput> => {
        const url = `${routes.requestBasePath}/deleteUploadedFile`;
        const response = await axiosInstance.post(url, input, config);
        return DeleteUploadedFileOutputSchema.parse(response.data);
    }

    const createAndSubmitRequest = async (input: InsertRequestInput, config?: AxiosRequestConfig): Promise<CreatedRequestOutput> => {
        const url = `${routes.requestBasePath}/createAndSubmitRequest`;
        const response = await axiosInstance.post(url, input, config);
        return CreatedRequestOutputSchema.parse(response.data);
    }

    const cancelRequest = async (input: RequestDetailInput, config?: AxiosRequestConfig): Promise<CreatedRequestOutput> => {
        const url = `${routes.requestBasePath}/cancelRequest`;
        const response = await axiosInstance.post(url, input, config);
        return CreatedRequestOutputSchema.parse(response.data);
    }

    const cancelRequestSigning = async (input: RequestDetailInput, config?: AxiosRequestConfig): Promise<void> => {
        const url = `${routes.requestBasePath}/cancelRequestSigning`;
        await axiosInstance.post(url, input, config);
    }

    const scanAttachmentWithAI = async (input: ScanAttachmentWithAIInput, config?: AxiosRequestConfig): Promise<ScanAttachmentWithAIOutput> => {
        const url = `${routes.requestBasePath}/scanAttachmentWithAI`;
        const response = await axiosInstance.post(url, input, config);
        return ScanAttachmentWithAIOutputSchema.parse(response.data);
    }

    const uploadFile = async (input: Record<string, any>, config?: AxiosRequestConfig): Promise<Record<string, any>> => {
        const url = `${routes.requestBasePath}/uploadFile`;
        const response = await axiosInstance.post(url, input, config);
        return response.data;
    }

    const changeFileTaxonomy = async (input: ChangeFileTaxonomyInput, config?: AxiosRequestConfig): Promise<ChangeFileTaxonomyOutput> => {
        const url = `${routes.requestBasePath}/changeFileTaxonomy`;
        const response = await axiosInstance.post(url, input, config);
        return ChangeFileTaxonomyOutputSchema.parse(response.data);
    }

    const getRequestsStates = async (input: GetRequestsStatesInput, config?: AxiosRequestConfig): Promise<boolean> => {
        const url = `${routes.requestBasePath}/getRequestsStates`;
        const response = await axiosInstance.post(url, input, config);
        if (typeof response.data === "boolean") return response.data;
        return false;
    }

    const getMagicLinkConfig = async (input: GetMagicLinkConfigInput, config?: AxiosRequestConfig): Promise<GetMagicLinkConfigOutput> => {
        const url = `${routes.magicLinkBasePath}/getMagicLinkConfig`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return GetMagicLinkConfigOutputSchema.parse(response.data);
    }

    const createMagicLink = async (input: CreateMagicLinkInput, config?: AxiosRequestConfig): Promise<CreateMagicLinkOutput> => {
        const url = `${routes.magicLinkBasePath}/createMagicLink`;
        const response = await axiosInstance.post(url, input, config);
        return CreateMagicLinkOutputSchema.parse(response.data);
    }

    const uploadIdCardWithValidation = async (input: UploadIdCardInput, config?: AxiosRequestConfig): Promise<UploadIdCardOutput> => {
        const url = `${routes.magicLinkBasePath}/uploadIdCardWithValidation`;
        const response = await axiosInstance.post(url, input, config);
        return UploadIdCardOutputSchema.parse(response.data);
    }

    // Manual signature shares the magic-link upload endpoint. See MagicLinkController.UploadIdCardWithValidation.
    const uploadManualSignatureIdCard = async (input: UploadIdCardInput, config?: AxiosRequestConfig): Promise<UploadIdCardOutput> => {
        const url = `${routes.magicLinkBasePath}/uploadIdCardWithValidation`;
        const response = await axiosInstance.post(url, input, config);
        return UploadIdCardOutputSchema.parse(response.data);
    }

    const startMagicLinkProcess = async (input: StartMagicLinkProcessInput, config?: AxiosRequestConfig): Promise<BaseOutput> => {
        const url = `${routes.magicLinkBasePath}/startMagicLinkProcess`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return BaseOutputSchema.parse(response.data);
    }

    const cancelMagicLinkProcess = async (input: CancelMagicLinkProcessInput, config?: AxiosRequestConfig): Promise<BaseOutput> => {
        const url = `${routes.magicLinkBasePath}/cancelMagicLinkProcess`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return BaseOutputSchema.parse(response.data);
    }

    const getAllRequests = async (input: GetAllRequestsInput, config?: AxiosRequestConfig): Promise<GetAllRequestsOutput> => {
        const url = `${routes.requestBasePath}/GetAllRequests`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return GetAllRequestsOutputSchema.parse(response.data);
    }


    const getRequestFieldsRelation = async (input: RequestFieldsRelationInput, config?: AxiosRequestConfig): Promise<RequestFieldsRelationOutput> => {
        const url = `${routes.requestBasePath}/GetRequestFieldsRelation`;
        const sanitizedInput = removeFalsyPropertiesFromObject(input);
        const response = await axiosInstance.post(url, sanitizedInput, config);
        return RequestFieldsRelationOutputSchema.parse(response.data);
    }

    return {
        getFormFieldData,
        getRequestPrepopInfo,
        getRequestContractPrepopInfo,
        getRequestPartnerPrepopInfo,
        getAllRequestsTypes,
        getDetail,
        saveOrUpdateRequest,
        deleteUploadedFile,
        createAndSubmitRequest,
        cancelRequest,
        cancelRequestSigning,
        scanAttachmentWithAI,
        uploadFile,
        changeFileTaxonomy,
        getRequestsStates,
        getMagicLinkConfig,
        createMagicLink,
        uploadIdCardWithValidation,
        uploadManualSignatureIdCard,
        startMagicLinkProcess,
        cancelMagicLinkProcess,
        getAllRequests,
        getRequestFieldsRelation
    };
}

export type UseRequestApi = ReturnType<typeof useRequestApi>;
